
import { useState } from 'react';
import { SearchResult } from '@/types/searchResults';

interface Message {
  id: number;
  content: string;
  type: 'user' | 'ai';
  timestamp: Date;
  model: string;
  isImage?: boolean;
  attachedMedia?: any;
  selectedAgent?: any;
  searchResults?: SearchResult;
}

interface RegenerateHandlerProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  setIsImageMode: React.Dispatch<React.SetStateAction<boolean>>;
  setAttachedMedia: React.Dispatch<React.SetStateAction<any>>;
  setSelectedAgent: React.Dispatch<React.SetStateAction<any>>;
  handleSend: () => Promise<void>;
}

export const useRegenerateHandler = ({
  messages,
  setMessages,
  setInputValue,
  setIsImageMode,
  setAttachedMedia,
  setSelectedAgent,
  handleSend
}: RegenerateHandlerProps) => {

  const handleRegenerate = async (messageId?: number) => {
    if (messages.length === 0) return;
    
    let targetUserMessage: Message | undefined;
    let cutoffIndex = -1;

    if (messageId) {
      // Find the AI message to regenerate
      const aiMessageIndex = messages.findIndex(msg => msg.id === messageId && msg.type === 'ai');
      
      if (aiMessageIndex >= 0) {
        // Find the user message that preceded this AI message
        for (let i = aiMessageIndex - 1; i >= 0; i--) {
          if (messages[i].type === 'user') {
            targetUserMessage = messages[i];
            cutoffIndex = aiMessageIndex; // Remove from AI message onwards
            break;
          }
        }
      }
    } else {
      // Default behavior - find last user message and regenerate its response
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].type === 'user') {
          targetUserMessage = messages[i];
          // Find where to cut off (after the user message)
          cutoffIndex = i + 1;
          break;
        }
      }
    }

    if (!targetUserMessage) {
      console.error('No user message found to regenerate from');
      return;
    }

    // Remove messages from the cutoff point onwards
    if (cutoffIndex >= 0 && cutoffIndex < messages.length) {
      setMessages(prev => prev.slice(0, cutoffIndex));
    }

    // Set up the regeneration with the target user message's context
    setInputValue(targetUserMessage.content);
    setIsImageMode(targetUserMessage.isImage || false);
    setAttachedMedia(targetUserMessage.attachedMedia || null);
    setSelectedAgent(targetUserMessage.selectedAgent || null);
    
    // Trigger regeneration after a brief delay to ensure state updates
    setTimeout(() => {
      handleSend();
    }, 100);
  };

  return { handleRegenerate };
};
